<template>
  <div class="customerSummaryPrescription">
    <div class="customerSummaryPrescription__header">
      <span class="customerSummaryPrescription__title">
        {{ $t('customer.summary.prescription.title') }}
      </span>
      <sl-tag v-if="prescription?.external" size="small">
        {{ $t('customer.summary.prescription.external_badge') }}
      </sl-tag>
    </div>
    <div v-if="loading" class="customerSummaryPrescription__loading">
      <sl-spinner></sl-spinner>
    </div>
    <div v-else-if="error" class="customerSummaryPrescription__empty">
      {{ $t('customer.summary.load_error') }}
    </div>
    <template v-else-if="prescription">
      <div class="customerSummaryPrescription__meta">
        {{ $t('customer.summary.prescription.full_correction') }} · {{ formattedDate }}
      </div>
      <table class="customerSummaryPrescription__table">
        <thead>
          <tr class="customerSummaryPrescription__tableHead">
            <td></td>
            <td>{{ $t('customer.summary.prescription.sph') }}</td>
            <td>{{ $t('customer.summary.prescription.cyl') }}</td>
            <td>{{ $t('customer.summary.prescription.axis') }}</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="customerSummaryPrescription__eye">R</td>
            <td>{{ formatValue(prescription.right?.spherical) }}</td>
            <td>{{ formatValue(prescription.right?.cylindrical) }}</td>
            <td>{{ formatValue(prescription.right?.axis) }}</td>
          </tr>
          <tr>
            <td class="customerSummaryPrescription__eye">L</td>
            <td>{{ formatValue(prescription.left?.spherical) }}</td>
            <td>{{ formatValue(prescription.left?.cylindrical) }}</td>
            <td>{{ formatValue(prescription.left?.axis) }}</td>
          </tr>
        </tbody>
      </table>
    </template>
    <div v-else class="customerSummaryPrescription__empty">
      {{ $t('customer.summary.prescription.empty') }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { formatISODateToDDMMYYYY } from '@/utils/dateTime.js'

const props = defineProps({
  prescription: {
    type: Object,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: Boolean,
    default: false
  }
})

const formattedDate = computed(() =>
  props.prescription?.date ? formatISODateToDDMMYYYY(props.prescription.date) : '–'
)

// Spherical / cylindrical come back as pre-signed strings ('+1.25', '-0.50'), axis as a
// number. Same empty handling as FormPrescriptionOutput.showValue: 0 is a real axis, '' is not
// a value. The mockup shows a dash rather than 0.00 for anything unset.
function formatValue(value) {
  if (value === 0) return 0
  return value === null || value === undefined || value === '' ? '–' : value
}
</script>

<style scoped>
.customerSummaryPrescription {
  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__title {
    font-size: var(--sl-font-size-x-small);
    color: var(--sl-color-neutral-500);
  }

  &__header sl-tag {
    margin-left: auto;
  }

  &__meta {
    font-size: var(--sl-font-size-small);
    margin-bottom: 6px;
  }

  &__table {
    width: 100%;
    font-size: var(--sl-font-size-small);
    table-layout: fixed;
    border-collapse: collapse;
  }

  &__table td {
    text-align: right;
    padding: 1px 0;
  }

  &__table td:first-child {
    text-align: left;
  }

  &__tableHead td {
    color: var(--sl-color-neutral-500);
    font-size: var(--sl-font-size-x-small);
  }

  &__eye {
    color: var(--sl-color-neutral-500);
  }

  &__empty {
    font-size: var(--sl-font-size-small);
    color: var(--sl-color-neutral-500);
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 12px 0;
  }
}
</style>
